import { API_BASE } from '../apiBase';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAddressSuggestions, geocodePlaceId } from '../lib/googlePlaces';

function Customers() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal Ref for click-outside detection
  const modalRef = useRef(null);

  // Add customer modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    name: '',
    businessName: '',
    type: 'Homeowner',
    status: 'Lead',
    primaryPhone: '',
    primaryEmail: '',
    secondaryPhone: '',
    altEmail: '',
    billingAddress: {
      street: '',
      city: '',
      state: 'CA',
      zip: ''
    },
    notes: '',
    tags: ''
  });
  const [addError, setAddError] = useState('');

  // Inline status editing
  const [editingStatusId, setEditingStatusId] = useState(null);

  // Address autocomplete states
  const [addressQuery, setAddressQuery] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressError, setAddressError] = useState(null);

  // Search & copy states
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedCell, setCopiedCell] = useState(null);

  // Context menu for delete
  const [contextMenu, setContextMenu] = useState(null);

  // Status colors
  const statusColors = {
    Active: 'bg-green-600/80 text-white',
    Vendor: 'bg-blue-600/80 text-white',
    Lead: 'bg-yellow-600/80 text-white',
    Inactive: 'bg-gray-600/80 text-white',
    Lost: 'bg-red-600/80 text-white'
  };

  // Close modal on outside click
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowAddModal(false);
      }
    };

    if (showAddModal) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showAddModal]);

  // Phone formatter - always forces xxx-xxx-xxxx
  const formatPhone = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length !== 10) return phone;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  // Debounce utility
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

// Fetch address suggestions from Google Places (new API first, legacy fallback;
  // errors are surfaced in the dropdown instead of failing silently)
  const fetchAddressSuggestions = debounce(async (query) => {
    if (query.length < 3 || isManualAddress) {
      setAddressSuggestions([]);
      setAddressError(null);
      setShowSuggestions(false);
      return;
    }
    setAddressLoading(true);
    const { predictions, error } = await getAddressSuggestions(query);
    setAddressLoading(false);
    setAddressError(error);
    setAddressSuggestions(predictions);
    setShowSuggestions(true);
  }, 400);

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE}/customers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCustomers(res.data);
      } catch (err) {
        console.error('Fetch customers error:', err);
        setError('Failed to load customers');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [navigate]);

  const filteredCustomers = useMemo(() => {
    if (!searchTerm.trim()) return customers;
    const lower = searchTerm.toLowerCase();
    return customers.filter(c =>
      c.name?.toLowerCase().includes(lower) ||
      c.nickname?.toLowerCase().includes(lower) ||
      c.businessName?.toLowerCase().includes(lower) ||
      c.primaryEmail?.toLowerCase().includes(lower) ||
      c.primaryPhone?.includes(searchTerm) ||
      c.billingAddress?.city?.toLowerCase().includes(lower) ||
      c.type?.toLowerCase().includes(lower) ||
      c.status?.toLowerCase().includes(lower)
    );
  }, [customers, searchTerm]);

  const copyToClipboard = async (text, rowId, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedCell({ rowId, field });
      setTimeout(() => setCopiedCell(null), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleRowClick = (customer) => {
    navigate(`/customers/${customer._id}`);
  };

  const handleContextMenu = (e, customer) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      customerId: customer._id
    });
  };

  const handleDelete = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer? This cannot be undone.')) return;

    try {
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API_BASE}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.status === 200 || res.data.success) {
        setCustomers(prev => prev.filter(c => c._id !== customerId));
        setContextMenu(null);
        alert('Customer deleted successfully!');
      } else {
        alert('Delete failed: ' + (res.data?.error || 'Unknown response'));
      }
    } catch (err) {
      alert('Failed to delete customer: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleStatusChange = async (customerId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/customers/${customerId}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(prev =>
        prev.map(c => c._id === customerId ? { ...c, status: newStatus } : c)
      );
    } catch (err) {
      alert('Failed to update status: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setEditingStatusId(null);
    }
  };

  // Click-to-pick status pill (same UX as the Contacts People tab) — replaces
  // the old inline <select>. Returns the pill + a colored-dot popover.
  const CUSTOMER_STATUSES = ['Lead', 'Active', 'Vendor', 'Inactive', 'Lost'];
  const renderStatusPill = (customer) => {
    const isOpen = editingStatusId === customer._id;
    return (
      <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setEditingStatusId(isOpen ? null : customer._id); }}
          className={`px-3 py-1 text-xs font-medium rounded-full cursor-pointer hover:ring-2 hover:ring-white/20 transition-all ${statusColors[customer.status] || 'bg-gray-600/80 text-white'}`}
          title="Click to change status"
        >
          {customer.status}
        </button>
        {isOpen && (
          <>
            <span className="fixed inset-0 z-30" onClick={(e) => { e.stopPropagation(); setEditingStatusId(null); }} />
            <div className="absolute z-40 top-8 left-0 bg-gray-900 border border-gray-700 rounded-xl shadow-xl p-1 flex flex-col min-w-[8rem]">
              {CUSTOMER_STATUSES.map(s => (
                <button
                  key={s}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setEditingStatusId(null); handleStatusChange(customer._id, s); }}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-800 text-left"
                >
                  <span className={`w-2 h-2 rounded-full ${(statusColors[s] || 'bg-gray-500').split(' ')[0]}`} />
                  <span className={`text-[12px] ${s === customer.status ? 'text-white font-semibold' : 'text-zinc-300'}`}>{s}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </span>
    );
  };

  const handleInputChange = (e, path = null) => {
    const { name, value } = e.target;
    if (path) {
      setNewCustomer(prev => ({
        ...prev,
        [path]: { ...prev[path], [name]: value }
      }));
    } else {
      setNewCustomer(prev => ({ ...prev, [name]: value }));
    }
  };

  // Format phone on blur
  const handlePhoneBlur = (e, field) => {
    const formatted = formatPhone(e.target.value);
    setNewCustomer(prev => ({
      ...prev,
      [field]: formatted
    }));
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.firstName.trim()) {
      setAddError('First Name is required');
      return;
    }

    // Combine for the required 'name' field in schema
    const fullName = `${newCustomer.firstName} ${newCustomer.middleName} ${newCustomer.lastName}`.replace(/\s+/g, ' ').trim();

    // Final formatting before saving
    const payload = {
      ...newCustomer,
      name: fullName,
      primaryPhone: formatPhone(newCustomer.primaryPhone),
      secondaryPhone: formatPhone(newCustomer.secondaryPhone)
    };

    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API_BASE}/customers`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCustomers(prev => [...prev, res.data]);

      setNewCustomer({
        firstName: '',
        middleName: '',
        lastName: '',
        name: '',
        businessName: '',
        type: 'Homeowner',
        status: 'Lead',
        primaryPhone: '',
        primaryEmail: '',
        secondaryPhone: '',
        altEmail: '',
        billingAddress: { street: '', city: '', state: 'CA', zip: '' },
        notes: '',
        tags: ''
      });
      setAddressQuery('');
      setAddressSuggestions([]);
      setAddError('');
      setShowAddModal(false);
      setIsManualAddress(false);
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to create customer');
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8">Loading customers...</div>;
  if (error) return <div className="min-h-screen bg-gray-950 text-zinc-100 p-8 text-red-400">{error}</div>;

  return (
    <div className="h-screen bg-gray-950 text-zinc-100 p-8 max-md:p-4 max-md:h-[calc(100dvh-3.5rem)] flex flex-col gap-6 max-md:gap-4 overflow-hidden">
      {/* Header */}
      <div className="shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
        <h1 className="text-3xl font-bold tracking-tight">Customers</h1>
        <button
          className="px-6 py-3 bg-indigo-700/90 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg hover:scale-[1.02]"
          onClick={() => setShowAddModal(true)}
        >
          + Add Customer
        </button>
      </div>

      {/* Search */}
      <div className="shrink-0 max-w-xl">
        <input
          type="text"
          placeholder="Search by name, email, phone, city, type, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full px-5 py-3.5 bg-gray-900 border border-gray-800 rounded-xl text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-indigo-600/60 focus:ring-2 focus:ring-indigo-600/20 transition-all"
        />
      </div>

      {/* ===== MOBILE CARD LIST (phones only — desktop table below untouched) ===== */}
      <div className="md:hidden flex-1 min-h-0 overflow-y-auto space-y-3 pb-4">
        {filteredCustomers.length === 0 ? (
          <p className="text-center text-zinc-500 pt-16">No customers found. Try adjusting your search.</p>
        ) : (
          filteredCustomers.map((customer) => (
            <div
              key={customer._id}
              onClick={() => handleRowClick(customer)}
              className="bg-gray-900 border border-gray-800/60 rounded-2xl p-4 active:bg-gray-800/70 transition-colors"
            >
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="text-zinc-100 font-semibold leading-snug min-w-0 break-words">{customer.name}</p>
                <span className="shrink-0">{renderStatusPill(customer)}</span>
              </div>
              {customer.type && <p className="text-zinc-500 text-xs">{customer.type}</p>}
              {customer.phone && (
                <a href={`tel:${customer.phone}`} onClick={(e) => e.stopPropagation()} className="block text-indigo-400 text-sm mt-1.5">
                  {formatPhone(customer.phone)}
                </a>
              )}
              {customer.email && (
                <a href={`mailto:${customer.email}`} onClick={(e) => e.stopPropagation()} className="block text-zinc-400 text-sm mt-0.5 break-all">
                  {customer.email}
                </a>
              )}
              {customer.billingAddress?.city && <p className="text-zinc-600 text-xs mt-1">{customer.billingAddress.city}</p>}
            </div>
          ))
        )}
      </div>

      {/* Table — only the rows scroll; header row stays via sticky thead */}
      <div className="max-md:hidden flex-1 min-h-0 flex flex-col bg-gray-900 rounded-2xl border border-gray-800/50 overflow-hidden shadow-2xl">
        <div className="flex-1 min-h-0 overflow-y-auto">
        <table className="min-w-full divide-y divide-gray-800/60">
          <thead className="bg-[#0d1321] sticky top-0 z-10">
            <tr>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Name</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Type</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Status</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Phone</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Email</th>
              <th className="px-8 py-5 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">City</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800/40">
            {filteredCustomers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-8 py-20 text-center text-zinc-500 text-lg">
                  No customers found. Try adjusting your search.
                </td>
              </tr>
            ) : (
              filteredCustomers.map((customer) => (
                <tr
                  key={customer._id}
                  className="bg-gray-900 hover:bg-gray-800/50 hover:shadow-sm transition-all duration-200 cursor-pointer group"
                  onContextMenu={(e) => handleContextMenu(e, customer)}
                  onClick={(e) => {
                    if (!e.target.closest('span') && !editingStatusId) {
                      handleRowClick(customer);
                    }
                  }}
                >
                  <td className="px-8 py-6 whitespace-nowrap text-sm font-medium text-zinc-100">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(customer.name, customer._id, 'name');
                      }}
                    >
                      {customer.name}
                      {copiedCell?.rowId === customer._id && copiedCell?.field === 'name' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                          Copied!
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">{customer.type}</td>

                  <td className="px-8 py-6 whitespace-nowrap">
                    {renderStatusPill(customer)}
                  </td>

                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(customer.primaryPhone, customer._id, 'phone');
                      }}
                    >
                      {formatPhone(customer.primaryPhone) || '—'}
                      {copiedCell?.rowId === customer._id && copiedCell?.field === 'phone' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                          Copied!
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    <span
                      className="cursor-pointer relative group inline-block"
                      onClick={(e) => {
                        e.stopPropagation();
                        copyToClipboard(customer.primaryEmail, customer._id, 'email');
                      }}
                    >
                      {customer.primaryEmail || '—'}
                      {copiedCell?.rowId === customer._id && copiedCell?.field === 'email' && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-zinc-200 text-xs px-3 py-1 rounded-md shadow-lg z-10 animate-fade-in-out">
                          Copied!
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-8 py-6 whitespace-nowrap text-sm text-zinc-300">
                    {customer.billingAddress?.city || '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Right-click Delete Menu */}
      {contextMenu && (
        <div
          className="fixed bg-gray-900 border border-gray-700 rounded-lg shadow-2xl py-2 z-50"
          style={{ top: `${contextMenu.y}px`, left: `${contextMenu.x}px` }}
        >
          <button
            className="w-full px-6 py-2 text-left text-red-400 hover:bg-gray-800 hover:text-red-300 transition-colors"
            onClick={() => handleDelete(contextMenu.customerId)}
          >
            Delete Customer
          </button>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div ref={modalRef} className="bg-gray-900 rounded-2xl p-8 w-full max-w-2xl border border-gray-700 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">Add New Customer</h2>
            {addError && <p className="text-red-400 mb-4">{addError}</p>}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Names on same horizontal plane */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1">First Name *</label>
                <input
                  name="firstName"
                  value={newCustomer.firstName}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                  required
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Middle Name</label>
                <input
                  name="middleName"
                  value={newCustomer.middleName}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Last Name</label>
                <input
                  name="lastName"
                  value={newCustomer.lastName}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-zinc-400 text-sm mb-1">Business Name <span className="text-zinc-600">(optional)</span></label>
              <input
                name="businessName"
                value={newCustomer.businessName}
                onChange={(e) => handleInputChange(e)}
                placeholder="e.g. McDonald's LLC — leave blank for individuals"
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200 placeholder-zinc-500"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
              {/* Status and Type */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Status</label>
                <select
                  name="status"
                  value={newCustomer.status}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                >
                  <option value="Lead">Lead</option>
                  <option value="Active">Active</option>
                  <option value="Vendor">Vendor</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Lost">Lost</option>
                </select>
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Type</label>
                <select
                  name="type"
                  value={newCustomer.type}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                >
                  <option value="Homeowner">Homeowner</option>
                  <option value="Contractor">Contractor</option>
                  <option value="Government">Government</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Property Manager">Property Manager</option>
                  <option value="HOA">HOA</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Phones */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Primary Phone</label>
                <input
                  name="primaryPhone"
                  value={newCustomer.primaryPhone}
                  onChange={(e) => handleInputChange(e)}
                  onBlur={(e) => handlePhoneBlur(e, 'primaryPhone')}
                  placeholder="xxx-xxx-xxxx"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Secondary Phone</label>
                <input
                  name="secondaryPhone"
                  value={newCustomer.secondaryPhone}
                  onChange={(e) => handleInputChange(e)}
                  onBlur={(e) => handlePhoneBlur(e, 'secondaryPhone')}
                  placeholder="xxx-xxx-xxxx"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>

              {/* Emails */}
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Primary Email</label>
                <input
                  name="primaryEmail"
                  type="email"
                  value={newCustomer.primaryEmail}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Alternate Email</label>
                <input
                  name="altEmail"
                  type="email"
                  value={newCustomer.altEmail}
                  onChange={(e) => handleInputChange(e)}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
            </div>

            {/* Billing Address */}
            <div className="mt-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Billing Address</h3>
                <button
                  type="button"
                  onClick={() => setIsManualAddress(!isManualAddress)}
                  className="text-indigo-400 text-sm hover:text-indigo-300 transition-colors"
                >
                  {isManualAddress ? 'Use Automatic Search' : 'Enter Address Manually'}
                </button>
              </div>

              <div className="space-y-4 relative">
                {!isManualAddress ? (
                  <div>
                    <label className="block text-zinc-400 text-sm mb-1">Address (type to search - free)</label>
                    <input
                      type="text"
                      value={addressQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setAddressQuery(val);
                        fetchAddressSuggestions(val);
                      }}
                      onFocus={() => setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 300)}
                      placeholder="Start typing street, city, or zip..."
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                    />
                    {addressLoading && (
                      <div className="absolute right-3 top-12 text-zinc-500 text-sm">Loading...</div>
                    )}
                    {showSuggestions && (
                      <div className="absolute w-full bg-gray-800 border border-gray-700 rounded-xl mt-1 max-h-60 overflow-auto z-50 shadow-xl">
                        {addressError ? (
                          <div className="px-4 py-3 text-red-400 text-sm">{addressError}</div>
                        ) : addressSuggestions.length === 0 ? (
                          <div className="px-4 py-3 text-zinc-500 text-sm">No matching addresses found</div>
                        ) : (
                          addressSuggestions.map((place, index) => (
                           <div
                              key={index}
                              className="px-4 py-3 hover:bg-gray-700 cursor-pointer text-zinc-200 transition-colors"
                              onClick={async () => {
                                // 1. Instantly show the clean Google text
                                setAddressQuery(place.description);
                                setShowSuggestions(false);

                                // 2. Break it into parts and save to billingAddress
                                try {
                                  const parts = await geocodePlaceId(place.placeId);
                                  setNewCustomer(prev => ({ ...prev, billingAddress: parts }));
                                } catch (e) {
                                  console.error('🚨 Could not parse selected address:', e.message);
                                }
                              }}
                            >
                              {place.description}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-800/50 p-4 rounded-xl">
                    <div className="md:col-span-2">
                      <label className="block text-zinc-400 text-xs mb-1">Street Address</label>
                      <input
                        name="street"
                        value={newCustomer.billingAddress.street}
                        onChange={(e) => handleInputChange(e, 'billingAddress')}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-zinc-200"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-xs mb-1">City</label>
                      <input
                        name="city"
                        value={newCustomer.billingAddress.city}
                        onChange={(e) => handleInputChange(e, 'billingAddress')}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-zinc-200"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-400 text-xs mb-1">State</label>
                      <input
                        name="state"
                        value={newCustomer.billingAddress.state}
                        onChange={(e) => handleInputChange(e, 'billingAddress')}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-zinc-200"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-zinc-400 text-xs mb-1">Zip Code</label>
                      <input
                        name="zip"
                        value={newCustomer.billingAddress.zip}
                        onChange={(e) => handleInputChange(e, 'billingAddress')}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-zinc-200"
                      />
                    </div>
                  </div>
                )}
                
                {!isManualAddress && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-zinc-400 bg-gray-800/50 p-4 rounded-xl">
                    <div><strong>Street:</strong> {newCustomer.billingAddress.street || '—'}</div>
                    <div><strong>City:</strong> {newCustomer.billingAddress.city || '—'}</div>
                    <div><strong>State:</strong> {newCustomer.billingAddress.state || '—'}</div>
                    <div><strong>Zip:</strong> {newCustomer.billingAddress.zip || '—'}</div>
                  </div>
                )}
              </div>
            </div>

            {/* Notes & Tags */}
            <div className="mt-8 space-y-6">
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Tags (comma separated)</label>
                <input
                  name="tags"
                  value={newCustomer.tags}
                  onChange={(e) => setNewCustomer(prev => ({ ...prev, tags: e.target.value }))}
                  placeholder="VIP, Repeat, High Value"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={newCustomer.notes}
                  onChange={(e) => handleInputChange(e)}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-zinc-200"
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-10">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-xl font-medium transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddCustomer}
                className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-medium shadow-md transition-all hover:shadow-lg"
              >
                Save Customer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copied animation */}
      <style>{`
        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(8px); }
          20% { opacity: 1; transform: translateY(0); }
          80% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        .animate-fade-in-out {
          animation: fadeInOut 3s ease-in-out forwards;
        }
      `}</style>
    </div>
  );
}

export default Customers;